import { useEffect, useState, useCallback } from "react";
import { Loader2, Scale, Save, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtGram, fmtDate } from "@/lib/format";
import { toast } from "sonner";

export default function AdminBulkGram() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      const pending = (data || []).filter((o) => !o.gram_finalized && !["İptal Edildi", "Reddedildi"].includes(o.status));
      setOrders(pending);
      const e = {};
      pending.forEach((o) => {
        e[o.id] = Object.fromEntries(o.items.map((it) => [it.product_id, Array.from({ length: it.quantity }, (_, i) => (it.actual_grams && it.actual_grams[i] != null) ? it.actual_grams[i] : "")]));
      });
      setEdits(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setVal = (oid, pid, idx, val) =>
    setEdits((p) => ({ ...p, [oid]: { ...(p[oid] || {}), [pid]: ((p[oid] || {})[pid] || []).map((g, i) => (i === idx ? val : g)) } }));

  const focusNext = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const inputs = Array.from(document.querySelectorAll('input[data-bulk-gram="1"]'));
    const i = inputs.indexOf(e.target);
    if (i > -1 && i + 1 < inputs.length) inputs[i + 1].focus();
  };

  const saveOrder = async (o) => {
    const g = edits[o.id] || {};
    const items = Object.entries(g).map(([pid, arr]) => ({ product_id: pid, grams: arr.map((x) => parseFloat(x)).filter((x) => !isNaN(x)) }));
    setSaving(o.id);
    try {
      const { data } = await api.put(`/admin/orders/${o.id}/actual-grams`, { items });
      if (data.gram_finalized) {
        toast.success(`${o.order_number} kesinleşti · müşteriye bildirim gönderildi`);
        setOrders((prev) => prev.filter((x) => x.id !== o.id));
      } else {
        toast.info(`${o.order_number} kaydedildi (eksik parça var)`);
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-gold" size={28} /></div>;

  return (
    <div data-testid="admin-bulk-gram">
      <h1 className="font-display text-3xl text-foreground mb-1 flex items-center gap-2"><Scale className="text-gold" size={26} /> Toplu Gram Girişi</h1>
      <p className="text-sm text-muted-foreground mb-6">İstanbul'dan gelen partinin gramlarını arka arkaya girin. Enter ile bir sonraki kutuya geçin. Tümü dolunca sipariş kesinleşir ve müşteriye e-posta gider.</p>

      {orders.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="bulk-gram-empty">
          <CheckCircle2 className="mx-auto mb-4 text-emerald-400" size={44} strokeWidth={1.2} />
          <p className="text-sm">Gram bekleyen sipariş yok. Hepsi kesinleşmiş. 🎉</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`bulk-order-${o.order_number}`} className="bg-surface border border-border rounded-md p-4">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <span className="font-mono text-sm text-gold">{o.order_number}</span>
                  <span className="text-sm text-foreground ml-2">{o.company_name}</span>
                  <span className="text-xs text-muted-foreground ml-2">{fmtDate(o.created_at)} · {o.status}</span>
                </div>
                <span className="text-xs text-amber-400">Tahmini has ~{fmtGram(o.total_has)} gr</span>
              </div>
              <div className="space-y-3">
                {o.items.map((it) => {
                  const arr = (edits[o.id] || {})[it.product_id] || [];
                  const sum = arr.reduce((s, x) => s + (parseFloat(x) || 0), 0);
                  return (
                    <div key={it.product_id} className="border-b border-border/40 pb-2">
                      <div className="text-[11px] text-muted-foreground font-mono mb-1">{it.code} · {it.name} · {it.quantity} adet{it.size ? ` · Boy ${it.size}` : ""} · İşç {it.milyem || 0} milyem</div>
                      <div className="flex flex-wrap items-center gap-2">
                        {arr.map((gval, idx) => (
                          <input
                            key={idx}
                            data-testid={`bulk-gram-${it.code}-${idx}`}
                            data-bulk-gram="1"
                            type="number"
                            step="0.01"
                            value={gval}
                            onKeyDown={focusNext}
                            onChange={(e) => setVal(o.id, it.product_id, idx, e.target.value)}
                            placeholder={`#${idx + 1} gr`}
                            className="w-20 h-8 rounded bg-obsidian border border-border text-foreground text-sm px-2 focus:outline-none focus:ring-1 focus:ring-gold"
                          />
                        ))}
                        <span className="text-xs text-gold self-center">= {fmtGram(sum)} gr</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end mt-3">
                <button
                  data-testid={`bulk-save-${o.order_number}`}
                  onClick={() => saveOrder(o)}
                  disabled={saving === o.id}
                  className="flex items-center gap-2 px-4 h-9 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors text-sm disabled:opacity-60"
                >
                  {saving === o.id ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Kaydet & Kesinleştir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
